/**
 * API Suite store — list of runnable Meraki operations.
 *
 * Read-only for now; the runner UI fires per-operation tasks via apiPost
 * inside the page when the user clicks "Run".
 */
import { create } from "zustand";
import { get as apiGet, ApiError } from "@/api/client";
import { ApiOperationListSchema, type ApiOperation } from "@/api/schemas/meraki";
import { MOCK_API_OPERATIONS } from "@/api/fixtures/meraki-api-operations";

interface State {
  data: ApiOperation[];
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;
  load: () => Promise<void>;
}

export const useMerakiApiSuite = create<State>((set, get) => ({
  data: [],
  status: "idle",
  error: null,

  load: async () => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    try {
      const raw = await apiGet<unknown>("/meraki/operations/");
      set({ data: ApiOperationListSchema.parse(raw), status: "ready" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({
        data: MOCK_API_OPERATIONS,
        status: "error",
        error: `${message} — showing fixture data`,
      });
    }
  },
}));
